'use client';

import { ReactNode } from 'react';
import {
  FileArchive,
  FileSpreadsheet,
  FileText,
  FileType,
  Download,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { fileExtensionLabel, fileNameFromUrl } from '@/lib/attachments';

const ICONS: Record<string, typeof FileText> = {
  PDF: FileType,
  DOC: FileText,
  DOCX: FileText,
  TXT: FileText,
  RTF: FileText,
  ODT: FileText,
  PAGES: FileText,
  XLS: FileSpreadsheet,
  XLSX: FileSpreadsheet,
  CSV: FileSpreadsheet,
  ODS: FileSpreadsheet,
  NUMBERS: FileSpreadsheet,
  ZIP: FileArchive,
};

interface DocumentAttachmentProps {
  url: string;
  /** Extra line under the name — who shared it, when. */
  meta?: ReactNode;
  /** Shown at the end of the row, e.g. a link back to the post. */
  action?: ReactNode;
  className?: string;
}

/**
 * One file, as a row you can open.
 *
 * `target="_blank"` here is deliberate and unlike the in-app links that were
 * changed to stay put: this navigates to a file on S3, not to a page of the
 * app. Opening it in place would drop the member out of the site entirely.
 */
export function DocumentAttachment({
  url,
  meta,
  action,
  className,
}: DocumentAttachmentProps) {
  const name = fileNameFromUrl(url);
  const ext = fileExtensionLabel(url);
  const Icon = ICONS[ext] ?? FileText;

  return (
    <div
      className={cn(
        'group relative flex items-center gap-3 rounded-xl border border-[#cdc4c1] bg-[#f2efe8] p-3 transition-colors hover:bg-[#eae7e0] has-[a:focus-visible]:ring-2 has-[a:focus-visible]:ring-[#554E49] has-[a:focus-visible]:ring-offset-2',
        className
      )}
    >
      <span
        aria-hidden="true"
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#786D67] text-white"
      >
        <Icon className="h-5 w-5" strokeWidth={1.8} />
      </span>

      <div className="min-w-0 flex-1">
        {/* Stretched link: the whole row is the target, but only the file name
            is in the tab order and read out, so there is one stop per file
            rather than a row of competing controls. */}
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-[#3D2817] outline-none after:absolute after:inset-0 hover:underline"
        >
          <span className="line-clamp-2 break-words">{name}</span>
          <span className="sr-only"> ({ext} file, opens in a new tab)</span>
        </a>
        <p className="mt-0.5 text-xs text-[#554E49]">
          <span className="font-semibold">{ext}</span>
          {meta ? <> · {meta}</> : null}
        </p>
      </div>

      {action ? (
        // Above the stretched link so it stays clickable in its own right.
        <div className="relative z-10 shrink-0">{action}</div>
      ) : (
        <Download
          aria-hidden="true"
          className="h-4 w-4 shrink-0 text-[#554E49] opacity-0 transition-opacity group-hover:opacity-100 group-has-[a:focus-visible]:opacity-100"
        />
      )}
    </div>
  );
}

export default DocumentAttachment;
